import type { ToastServiceMethods } from 'primevue/toastservice';

import type { App } from 'vue';

import Aura from '@primeuix/themes/aura';
import AutoComplete from 'primevue/autocomplete';
import Chip from 'primevue/chip';
import PrimeVue, { type PrimeVueConfiguration } from 'primevue/config';
import ConfirmationService from 'primevue/confirmationservice';
import DialogService from 'primevue/dialogservice';
import MultiSelect from 'primevue/multiselect';
import Select from 'primevue/select';
import ToastService from 'primevue/toastservice';
import Tooltip from 'primevue/tooltip';
import {
	THEME_PRIMARY_COLOR_CUSTOM_KEY,
	THEME_PRIMARY_COLOR_KEY,
	THEME_SURFACE_COLOR_CUSTOM_KEY,
	THEME_SURFACE_COLOR_KEY,
} from 'src/stores/userInterfaceSettings/localStorageKeys';
import { applyTheme } from 'src/theme/applyTheme';
import {
	DEFAULT_CUSTOM_PRIMARY_HEX,
	DEFAULT_CUSTOM_SURFACE_HEX,
	resolvePrimaryColorName,
	resolveStoredHexColor,
	resolveSurfaceColorName,
} from 'src/theme/themeConfig';

import { notifyService } from 'src/services/notify/NotifyService';

import { getDateFormatKey, pickerDateToken } from 'src/util/date/dateFormatOptions';

interface PrimeVueInstance {
	config: {
		locale: Record<string, unknown>;
	};
}

interface I18nInstance {
	locale: string;
}

interface PrimeVueGlobalProperties {
	$i18n?: I18nInstance;
	$primevue?: PrimeVueInstance;
	$toast?: ToastServiceMethods;
}

const DE_PRIMEVUE_LOCALE = {
	accept: 'Ja',
	choose: 'Wählen',
	clear: 'Löschen',
	dayNames: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
	dayNamesMin: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
	dayNamesShort: ['Son', 'Mon', 'Die', 'Mit', 'Don', 'Fre', 'Sam'],
	emptyFilterMessage: 'Keine Treffer',
	emptyMessage: 'Keine Einträge verfügbar',
	emptySearchMessage: 'Keine Treffer',
	emptySelectionMessage: 'Keine Auswahl',
	firstDayOfWeek: 1,
	monthNames: [
		'Januar',
		'Februar',
		'März',
		'April',
		'Mai',
		'Juni',
		'Juli',
		'August',
		'September',
		'Oktober',
		'November',
		'Dezember',
	],
	monthNamesShort: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
	reject: 'Nein',
	today: 'Heute',
	weekHeader: 'KW',
};

const EN_PRIMEVUE_LOCALE = {
	accept: 'Yes',
	choose: 'Choose',
	clear: 'Clear',
	dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
	dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
	dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
	emptyFilterMessage: 'No results found',
	emptyMessage: 'No available options',
	emptySearchMessage: 'No results found',
	emptySelectionMessage: 'No selected item',
	firstDayOfWeek: 0,
	monthNames: [
		'January',
		'February',
		'March',
		'April',
		'May',
		'June',
		'July',
		'August',
		'September',
		'October',
		'November',
		'December',
	],
	monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
	reject: 'No',
	today: 'Today',
	weekHeader: 'Wk',
};

export function applyPrimeVueLocale(localeConfig: Record<string, unknown>, locale: string): void {
	Object.assign(localeConfig, locale.startsWith('de') ? DE_PRIMEVUE_LOCALE : EN_PRIMEVUE_LOCALE);
	localeConfig.dateFormat = pickerDateToken(getDateFormatKey());
}

export function bootPrimeVue({ app }: { app: App<Element> }): void {
	type Constructor = new (...args: unknown[]) => unknown;
	type PropDescriptor = { default?: unknown; type?: Constructor | Constructor[] };
	type PropEntry = Constructor | Constructor[] | PropDescriptor | undefined;
	type WithProps = { props?: Record<string, PropEntry> };
	type WithExtends = { extends?: { extends?: WithProps } & WithProps } & WithProps;
	const setComponentPropDefault = (component: unknown, name: string, value: unknown): void => {
		let current: WithExtends | undefined = component as WithExtends;
		while (current) {
			if (current.props && name in current.props) {
				const existing = current.props[name];
				if (typeof existing === 'function' || Array.isArray(existing)) {
					current.props[name] = { default: value, type: existing };
				} else if (existing && typeof existing === 'object') {
					existing.default = value;
				}
				return;
			}
			current = current.extends;
		}
	};
	const bootLocale = (app.config.globalProperties as PrimeVueGlobalProperties).$i18n?.locale ?? 'de-DE';
	const filterPlaceholder = bootLocale.startsWith('de') ? 'Suchen…' : 'Search…';
	const autoFilterFocusDefault = true;
	setComponentPropDefault(Select, 'focusOnHover', false);
	setComponentPropDefault(Select, 'filter', true);
	setComponentPropDefault(Select, 'autoFilterFocus', autoFilterFocusDefault);
	setComponentPropDefault(Select, 'resetFilterOnHide', true);
	setComponentPropDefault(Select, 'filterPlaceholder', filterPlaceholder);
	setComponentPropDefault(MultiSelect, 'autoFilterFocus', autoFilterFocusDefault);
	setComponentPropDefault(AutoComplete, 'focusOnHover', false);

	setComponentPropDefault(Chip, 'removeIcon', 'fa-solid fa-xmark');
	setComponentPropDefault(MultiSelect, 'chipIcon', 'fa-solid fa-xmark');

	const primary = resolvePrimaryColorName(localStorage.getItem(THEME_PRIMARY_COLOR_KEY));
	const surface = resolveSurfaceColorName(localStorage.getItem(THEME_SURFACE_COLOR_KEY));
	const customPrimaryHex = resolveStoredHexColor(
		localStorage.getItem(THEME_PRIMARY_COLOR_CUSTOM_KEY),
		DEFAULT_CUSTOM_PRIMARY_HEX
	);
	const customSurfaceHex = resolveStoredHexColor(
		localStorage.getItem(THEME_SURFACE_COLOR_CUSTOM_KEY),
		DEFAULT_CUSTOM_SURFACE_HEX
	);

	const primeVueConfiguration: PrimeVueConfiguration = {
		theme: {
			options: {
				cssLayer: false,
				darkModeSelector: '.body--dark',
			},
			preset: Aura,
		},
		zIndex: {
			menu: 1000,
			modal: 1100,
			overlay: 1200,
			tooltip: 1300,
		},
	};

	app.use(PrimeVue, primeVueConfiguration);
	app.use(ToastService);
	app.use(ConfirmationService);
	app.use(DialogService);
	app.directive('tooltip', Tooltip);

	applyTheme(primary, surface, customPrimaryHex, customSurfaceHex);

	const globals = app.config.globalProperties as PrimeVueGlobalProperties;
	const primeVueInstance = globals.$primevue;
	if (primeVueInstance?.config?.locale) {
		applyPrimeVueLocale(primeVueInstance.config.locale, globals.$i18n?.locale ?? 'de-DE');
	}

	if (globals.$toast !== undefined) {
		notifyService.register(globals.$toast);
	}
}
